import { useState } from "react";
import LegalLayout from "./LegalLayout";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

export default function ServiceContract() {
  const { user } = useAuth();
  const [form, setForm] = useState({
    full_name: user?.name || "",
    legal_id: "", // RFC / NIF / CURP / DNI
    company: "",
    email: user?.email || "",
    plan: "pro",
    signed_at: new Date().toISOString().slice(0, 10),
    acceptance: false,
    lang: "es", // "es" = México / "en" = Internacional (Delaware / AAA)
  });
  const [submitted, setSubmitted] = useState(null);
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.acceptance) {
      toast.error("Debes aceptar el contrato antes de firmar");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/legal/sign-contract", form);
      setSubmitted(data);
      toast.success("Contrato firmado — te enviamos una copia a tu correo");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error al firmar el contrato");
    } finally {
      setBusy(false);
    }
  };

  if (submitted) {
    const apiBase = process.env.REACT_APP_BACKEND_URL;
    const pdfUrl = `${apiBase}${submitted.pdf_url || `/api/legal/contracts/${submitted.contract_id}/pdf`}`;
    return (
      <LegalLayout title="Contrato firmado" updatedAt="AHORA">
        <div className="bg-[#FFD600] border border-[#0A0A0A] p-6" data-testid="contract-signed">
          <div className="font-display text-3xl">✓ Contrato firmado con éxito</div>
          <div className="mt-3 font-mono-data text-sm">
            <p>Folio: <b>{submitted.contract_id}</b></p>
            <p>Firmado por: <b>{submitted.full_name}</b> ({submitted.email})</p>
            <p>Fecha: <b>{submitted.signed_at}</b></p>
            <p>Hash SHA-256 del documento:</p>
            <code className="block bg-white border border-[#0A0A0A] p-2 mt-1 text-[10px] break-all">
              {submitted.document_hash}
            </code>
          </div>
          <div className="mt-6 flex flex-wrap gap-3">
            <a
              href={pdfUrl}
              target="_blank"
              rel="noopener noreferrer"
              download={`contrato-${submitted.contract_id}.pdf`}
              className="inline-flex items-center gap-2 bg-[#0A0A0A] text-white px-5 py-3 font-mono-mark text-xs border border-[#0A0A0A] hover:bg-[#E53935] hover:border-[#E53935]"
              data-testid="contract-download-pdf"
            >
              DESCARGAR PDF FIRMADO
            </a>
            <a
              href={pdfUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 bg-white text-[#0A0A0A] px-5 py-3 font-mono-mark text-xs border border-[#0A0A0A] hover:bg-[#F4F4F0]"
              data-testid="contract-view-pdf"
            >
              VER PDF EN EL NAVEGADOR
            </a>
          </div>
          <p className="mt-6 font-mono-mark text-[10px] text-[#5C5C5C]">
            GUARDA EL PDF EN UN LUGAR SEGURO · EL ARCHIVO INCLUYE EL HASH + FOLIO + TU HUELLA DE FIRMA (IP, NAVEGADOR, FECHA).
          </p>
        </div>
      </LegalLayout>
    );
  }

  return (
    <LegalLayout title="Contrato de Prestación de Servicios" updatedAt="20 DE FEBRERO DE 2026">
      <p>
        <b>Contrato SaaS</b> que se celebra entre <b>CIA Labs S.A.P.I. de C.V.</b>
        (en adelante "EL PRESTADOR") y el cliente que suscribe la Plataforma CIA
        Marketing® (en adelante "EL CLIENTE"), de conformidad con las siguientes:
      </p>

      <h2>DECLARACIONES</h2>
      <p>
        <b>I. EL PRESTADOR</b> declara ser una sociedad mercantil legalmente
        constituida bajo las leyes de México, con RFC{" "}
        <i>CLA260101XXX</i>, dedicada al desarrollo y comercialización de software
        de automatización de marketing con inteligencia artificial.
      </p>
      <p>
        <b>II. EL CLIENTE</b> declara contar con la capacidad legal y recursos para
        celebrar este contrato, y que los datos proporcionados en el formulario de
        firma son veraces.
      </p>

      <h2>CLÁUSULAS</h2>

      <h3>PRIMERA — Objeto</h3>
      <p>
        EL PRESTADOR otorga a EL CLIENTE una <b>licencia no exclusiva, no
        transferible, revocable y limitada</b> para acceder y usar la Plataforma CIA
        Marketing® bajo el plan contratado, durante la vigencia del periodo de
        suscripción pagado.
      </p>

      <h3>SEGUNDA — Vigencia</h3>
      <p>
        El contrato entra en vigor a partir de la fecha de firma y se mantendrá
        vigente durante todo el periodo de suscripción activa, renovándose
        automáticamente por periodos iguales salvo notificación en contrario con 15
        días de antelación.
      </p>

      <h3>TERCERA — Contraprestación</h3>
      <p>
        EL CLIENTE pagará las tarifas vigentes del plan seleccionado conforme a lo
        publicado en <a href="/pricing">ciamkt.com/pricing</a>. Los pagos se
        realizan por adelantado vía Stripe. La falta de pago en tiempo suspenderá el
        acceso tras 7 días de gracia.
      </p>

      <h3>CUARTA — Obligaciones de EL PRESTADOR</h3>
      <ul>
        <li>Mantener la Plataforma disponible con un SLA de 99.5% mensual.</li>
        <li>Respaldar los datos del CLIENTE diariamente con retención de 30 días.</li>
        <li>Proporcionar soporte técnico en días y horas hábiles.</li>
        <li>Implementar medidas de seguridad estándar de la industria (TLS 1.3, AES-256, 2FA, etc.).</li>
        <li>Notificar cualquier brecha de seguridad en un plazo máximo de 72 horas.</li>
      </ul>

      <h3>QUINTA — Obligaciones de EL CLIENTE</h3>
      <ul>
        <li>Pagar puntualmente las contraprestaciones.</li>
        <li>Usar la Plataforma únicamente para fines lícitos.</li>
        <li>No compartir credenciales ni revender el acceso.</li>
        <li>Mantener actualizada su información de contacto y facturación.</li>
      </ul>

      <h3>SEXTA — Propiedad intelectual</h3>
      <p>
        La Plataforma, su código, diseño y marca son propiedad exclusiva de EL
        PRESTADOR. El contenido generado por EL CLIENTE (textos, imágenes, videos)
        es propiedad de EL CLIENTE, quien otorga a EL PRESTADOR una licencia
        gratuita y no exclusiva para procesarlos y transmitirlos a efectos de
        prestar el Servicio.
      </p>

      <h3>SÉPTIMA — Confidencialidad y protección de datos</h3>
      <p>
        Ambas partes se obligan a guardar confidencialidad sobre información que
        intercambien. EL PRESTADOR tratará los datos personales conforme a la{" "}
        <a href="/legal/privacy">Política de Privacidad</a> y el <b>Acuerdo de
        Procesamiento de Datos (DPA)</b>, disponible bajo solicitud a{" "}
        <a href="mailto:legal@ciamkt.com">legal@ciamkt.com</a>.
      </p>

      <h3>OCTAVA — Limitación de responsabilidad</h3>
      <p>
        La responsabilidad máxima acumulada de EL PRESTADOR frente a EL CLIENTE no
        excederá el importe pagado por este último en los últimos 12 meses. No hay
        responsabilidad por daños indirectos, lucro cesante ni pérdida de datos
        imputable a terceros.
      </p>

      <h3>NOVENA — Terminación</h3>
      <p>
        Cualquiera de las partes podrá dar por terminado el contrato:
      </p>
      <ul>
        <li>EL CLIENTE, en cualquier momento desde el dashboard (sin derecho a reembolso salvo excepciones de la Política de Reembolso).</li>
        <li>EL PRESTADOR, con 15 días de notificación por incumplimiento material del CLIENTE (impago, uso indebido, fraude).</li>
      </ul>
      <p>Tras la terminación, EL CLIENTE tendrá 30 días para exportar sus datos.</p>

      <h3>DÉCIMA — Ley aplicable y jurisdicción</h3>
      <p>
        Este contrato se rige por las leyes mexicanas. Cualquier controversia se
        someterá a los tribunales de Ciudad de México, renunciando las partes a
        cualquier otro fuero.
      </p>

      <h2 className="mt-12">Firma electrónica</h2>
      <p>
        Al completar este formulario y marcar la casilla de aceptación, EL CLIENTE
        declara haber leído y comprendido el contrato, otorgando su consentimiento
        con <b>firma electrónica simple</b> válida en términos del artículo 89 del
        Código de Comercio mexicano y la <b>eIDAS</b> europea.
      </p>

      <form onSubmit={submit} className="mt-8 bg-white border border-[#0A0A0A] p-6 space-y-4" data-testid="contract-form">
        <Row>
          <Field label="Nombre completo / Razón social" value={form.full_name} onChange={(v) => setForm({ ...form, full_name: v })} testid="contract-name" required />
          <Field label="RFC / NIF / DNI" value={form.legal_id} onChange={(v) => setForm({ ...form, legal_id: v })} testid="contract-legal-id" />
        </Row>
        <Row>
          <Field label="Empresa (opcional)" value={form.company} onChange={(v) => setForm({ ...form, company: v })} testid="contract-company" />
          <Field label="Email" type="email" value={form.email} onChange={(v) => setForm({ ...form, email: v })} testid="contract-email" required />
        </Row>
        <Row>
          <SelectField label="Plan contratado" value={form.plan} onChange={(v) => setForm({ ...form, plan: v })} options={[["free","Inicial"],["basic","Básico"],["pro","Pro"],["agency","Agencia"]]} testid="contract-plan" />
          <Field label="Fecha de firma" type="date" value={form.signed_at} onChange={(v) => setForm({ ...form, signed_at: v })} testid="contract-date" required />
        </Row>

        <div className="border border-[#0A0A0A] bg-[#F4F4F0] p-4">
          <label className="font-mono-mark text-[10px] text-[#0A0A0A]">JURISDICCIÓN / JURISDICTION</label>
          <div className="mt-2 grid md:grid-cols-2 gap-2">
            <label className="flex items-start gap-2 cursor-pointer border border-[#0A0A0A] bg-white p-3 hover:bg-[#FFD600]">
              <input
                type="radio"
                name="contract-lang"
                value="es"
                checked={form.lang === "es"}
                onChange={() => setForm({ ...form, lang: "es" })}
                className="mt-1"
                data-testid="contract-lang-es"
              />
              <span className="font-mono-data text-xs">
                <b>ESPAÑOL · MÉXICO 🇲🇽</b><br />
                <span className="text-[#5C5C5C]">Regido por leyes mexicanas. Tribunales de CDMX. Facturación CFDI.</span>
              </span>
            </label>
            <label className="flex items-start gap-2 cursor-pointer border border-[#0A0A0A] bg-white p-3 hover:bg-[#FFD600]">
              <input
                type="radio"
                name="contract-lang"
                value="en"
                checked={form.lang === "en"}
                onChange={() => setForm({ ...form, lang: "en" })}
                className="mt-1"
                data-testid="contract-lang-en"
              />
              <span className="font-mono-data text-xs">
                <b>ENGLISH · INTERNATIONAL 🌐</b><br />
                <span className="text-[#5C5C5C]">Delaware (USA) governing law. AAA arbitration. GDPR/CCPA/LGPD compliant.</span>
              </span>
            </label>
          </div>
        </div>

        <label className="flex items-start gap-3 pt-2 cursor-pointer">
          <input type="checkbox" checked={form.acceptance} onChange={(e) => setForm({ ...form, acceptance: e.target.checked })} className="mt-1" data-testid="contract-accept" />
          <span className="font-mono-data text-sm">
            Declaro haber leído y aceptado íntegramente el presente <b>Contrato de Prestación de Servicios</b>, los{" "}
            <a href="/legal/terms" target="_blank" rel="noopener noreferrer">Términos y Condiciones</a> y la{" "}
            <a href="/legal/privacy" target="_blank" rel="noopener noreferrer">Política de Privacidad</a>. Otorgo mi consentimiento expreso para su firma electrónica.
          </span>
        </label>

        <button
          type="submit"
          disabled={busy || !form.acceptance}
          className="w-full bg-[#E53935] text-white py-4 font-mono-mark text-xs border border-[#E53935] hover:bg-[#0A0A0A] hover:border-[#0A0A0A] disabled:opacity-50"
          data-testid="contract-submit"
        >
          {busy ? "FIRMANDO…" : "FIRMAR CONTRATO ELECTRÓNICAMENTE"}
        </button>
      </form>
    </LegalLayout>
  );
}

function Row({ children }) {
  return <div className="grid md:grid-cols-2 gap-4">{children}</div>;
}

function Field({ label, value, onChange, type = "text", required, testid }) {
  return (
    <div>
      <label className="font-mono-mark text-[10px] text-[#5C5C5C]">{label}{required && " *"}</label>
      <input
        type={type}
        value={value}
        required={required}
        onChange={(e) => onChange(e.target.value)}
        className="mt-1 w-full bg-white border border-[#0A0A0A] px-3 py-2 font-mono-data text-sm"
        data-testid={testid}
      />
    </div>
  );
}

function SelectField({ label, value, onChange, options, testid }) {
  return (
    <div>
      <label className="font-mono-mark text-[10px] text-[#5C5C5C]">{label}</label>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="mt-1 w-full bg-white border border-[#0A0A0A] px-3 py-2 font-mono-data text-sm"
        data-testid={testid}
      >
        {options.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
      </select>
    </div>
  );
}
